/* ------------------------------------------------------------------ *
 * Funnel v3 — orchestrator
 *
 * Wires:
 *   - PDP ATC click → multipack popup chain (A → B → cart_open
 *                     | A.decline/B.decline → FC_MULTIPACK → cart_open)
 *   - Cart drawer checkout click → black popup chain (v3.1, gated off)
 *   - BOGOF qty-mirror across split cart lines (lifted from custom.js
 *     with the _bogofplus/_bogoplus class-name typo fixed)
 *
 * Reads window.Funnel.product (emitted by sections/main-product.liquid).
 *
 * Cart contract:
 *   ATC                              → cart/add  qty=1   (silent, no drawer)
 *   MULTIPACK_A accept               → cart/change line qty=2
 *   MULTIPACK_B accept               → cart/change line qty=4
 *   FC_MULTIPACK accept              → cart/add  qty=1 + properties[_offer]=finalchance
 *   BLACK_1 accept (v3.1)            → cart/add  qty=1 + properties[related]=true
 *   FC_BLACK accept (v3.1)           → cart/add  qty=1 + properties[_offer]=finalchance
 *
 * Banned: properties[related]=true on ANY multipack line. Ever.
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  // ───── singleton guard ─────
  if (window.Funnel && window.Funnel._booted) return;
  window.Funnel = window.Funnel || {};
  window.Funnel._booted = true;

  // ───── config / constants ─────
  // inkfactory runs in BOGOF mode: anchor adds qty=2 (R1 buy-1-get-1-free),
  // the single upsell is "Double it" (qty 2→4), and the black cross-sell is
  // itself BOGOF (add qty=2), not R3/related. No MULTIPACK_A/B here.
  const STATES = Object.freeze({
    IDLE: 'idle',
    DOUBLE_IT: 'double-it',
    FC_MULTIPACK: 'fc-multipack',
    BLACK_1: 'black-1',
    FC_BLACK: 'fc-black',
    VIP: 'vip',
  });

  // ───── store config (from theme settings, emitted by funnel-modal) ─────
  const cfg = (window.Funnel.config = window.Funnel.config || {});
  // Double it is toggleable — ships OFF until the incentive price is decided.
  const DOUBLE_IT_ENABLED = !!cfg.doubleItEnabled;

  const SWAP_MS = 150;
  const DBG = !!window.Funnel.debug;
  const log = (...a) => { if (DBG) console.log('[funnel]', ...a); };

  // ───── product context (from PDP) ─────
  // window.Funnel.product = {
  //   id, variantId, handle, price (cents), tags: [], image,
  //   finalChancePrice (cents | null)
  // }
  const product = window.Funnel.product || null;

  // ───── DOM refs ─────
  const modal = document.getElementById('funnel-modal');
  const body = modal ? modal.querySelector('[data-funnel-body]') : null;

  if (!modal || !body) {
    log('modal element missing — orchestrator inert');
  }

  // ───── money formatting (Shopify-style {{amount}} replacement) ─────
  const moneyFormat = window.Funnel.moneyFormat || '€{{amount}}';

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    const amount = (cents / 100);
    const withDecimals = amount.toFixed(2);
    const withComma = withDecimals.replace('.', ',');
    return moneyFormat
      .replace('{{amount}}', withDecimals)
      .replace('{{amount_no_decimals}}', Math.round(amount).toString())
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(amount).toString());
  }

  // ───── cart helpers ─────
  async function cartAdd(variantId, qty, properties) {
    const body = { id: String(variantId), quantity: qty };
    if (properties && Object.keys(properties).length) body.properties = properties;
    log('cart/add', body);
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('cart/add failed: ' + res.status + ' ' + text);
    }
    return res.json();
  }

  async function cartChangeByLine(line, qty) {
    log('cart/change', { line, qty });
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line, quantity: qty }),
    });
    if (!res.ok) throw new Error('cart/change failed: ' + res.status);
    return res.json();
  }

  /**
   * Set the TOTAL quantity for a variant across all of its non-FC cart lines.
   *
   * Why this exists: discount Functions (R2/R5) split a single line into a
   * paid line + a free split line at the cart layer. If we only update the
   * paid line via cart/change, the free split line remains and totals drift
   * (e.g. accept-A then accept-B yields qty=5 instead of 4).
   *
   * Strategy: collect every cart-line key matching this variant (excluding
   * any line that carries _offer=finalchance — those belong to R4 and must
   * be preserved as their own thing), then issue one /cart/update.js call:
   *   - first matching key → targetQty
   *   - all other matching keys → 0 (removed)
   *
   * Falls back to /cart/add.js if the variant isn't in cart yet.
   */
  async function setVariantTotalQty(variantId, targetQty, properties) {
    const cart = await fetchCart();
    const matches = cart.items.filter(i =>
      Number(i.variant_id) === Number(variantId) &&
      !(i.properties && String(i.properties._offer || '').toLowerCase() === 'finalchance')
    );

    // If properties must be stamped (e.g. _offer=doubleit), cart/update can't
    // set properties — so zero out the existing lines and re-add fresh with them.
    if (properties && Object.keys(properties).length) {
      if (matches.length) {
        const clear = {};
        matches.forEach(item => { clear[item.key] = 0; });
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: clear }),
        });
      }
      return cartAdd(variantId, targetQty, properties);
    }

    if (matches.length === 0) {
      return cartAdd(variantId, targetQty);
    }

    const updates = {};
    matches.forEach((item, idx) => {
      updates[item.key] = idx === 0 ? targetQty : 0;
    });

    log('cart/update', updates);
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error('cart/update failed: ' + res.status + ' ' + text);
    }
    return res.json();
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
    return res.json();
  }

  function findLineByVariant(cart, variantId, opts) {
    opts = opts || {};
    const id = Number(variantId);
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      if (Number(item.variant_id) !== id) continue;
      // by default, ignore lines that carry an _offer property (FC lines)
      if (opts.skipOffer !== false && item.properties && item.properties._offer) continue;
      return { item, line: i + 1 }; // Shopify cart/change uses 1-based "line"
    }
    return null;
  }

  function cartHasFinalChanceLine(cart) {
    return cart.items.some(i =>
      i.properties && String(i.properties._offer || '').toLowerCase() === 'finalchance'
    );
  }

  function dispatchCartDrawerRefresh() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
  }

  function dispatchCartDrawerOpen() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
  }

  // ───── modal manager ─────
  let currentState = STATES.IDLE;
  let busy = false;

  function hydrate(content, data) {
    // data-slot fills
    content.querySelectorAll('[data-slot]').forEach(el => {
      const key = el.dataset.slot;
      if (!(key in data)) return;
      const v = data[key];
      if (el.tagName === 'IMG') {
        el.setAttribute('src', v || '');
        if (data.imageAlt) el.setAttribute('alt', data.imageAlt);
      } else {
        el.textContent = v == null ? '' : v;
      }
    });
  }

  function renderState(state, data) {
    const tpl = document.querySelector(`[data-funnel-template="${state}"]`);
    if (!tpl) { log('no template for', state); return null; }
    const frag = tpl.content.cloneNode(true);
    const wrapper = document.createElement('div');
    wrapper.appendChild(frag);
    hydrate(wrapper, data || {});
    return wrapper;
  }

  async function setState(state, data) {
    if (!modal || !body) return;

    const incoming = renderState(state, data);
    if (!incoming) return;

    if (currentState === STATES.IDLE) {
      // first open — no fade, just open
      body.innerHTML = '';
      while (incoming.firstChild) body.appendChild(incoming.firstChild);
      currentState = state;
      modal.setAttribute('open', '');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden'; // freeze background scroll
      bindActions();
      return;
    }

    // swap with fade
    modal.setAttribute('data-swapping', '');
    await new Promise(r => setTimeout(r, SWAP_MS));
    body.innerHTML = '';
    while (incoming.firstChild) body.appendChild(incoming.firstChild);
    currentState = state;
    bindActions();
    requestAnimationFrame(() => modal.removeAttribute('data-swapping'));
  }

  function closeModal() {
    if (!modal) return;
    modal.removeAttribute('open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    currentState = STATES.IDLE;
    body.innerHTML = '';
  }

  function bindActions() {
    if (!body) return;
    body.querySelectorAll('[data-funnel-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        if (busy) return;
        const action = btn.getAttribute('data-funnel-action');
        handleAction(action);
      }, { once: true });
    });
    // qty stepper (used by black popups)
    body.querySelectorAll('[data-funnel-qty-action]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const stepper = btn.closest('[data-funnel-qty]');
        if (!stepper) return;
        const min = parseInt(stepper.dataset.min || '1', 10);
        const max = parseInt(stepper.dataset.max || '5', 10);
        const input = stepper.querySelector('.funnel-card__qty-input');
        if (!input) return;
        let v = parseInt(input.value, 10);
        if (!Number.isFinite(v)) v = min;
        if (btn.dataset.funnelQtyAction === 'incr') v = Math.min(max, v + 1);
        else v = Math.max(min, v - 1);
        input.value = String(v);
        refreshBlackTotals();
      });
    });
  }

  function readStepperQty(stepper) {
    const input = stepper.querySelector('.funnel-card__qty-input');
    const v = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(v) && v > 0 ? v : 1;
  }

  function readCurrentBlackQty() {
    const stepper = body && body.querySelector('[data-funnel-qty]');
    return stepper ? readStepperQty(stepper) : 1;
  }

  // ───── transitions ─────
  async function handleAction(action) {
    log('action', currentState, action);
    try {
      busy = true;
      if (currentState === STATES.DOUBLE_IT) {
        if (action === 'accept') await onDoubleItAccept();
        else await onDoubleItDecline();
      } else if (currentState === STATES.FC_MULTIPACK) {
        if (action === 'accept') await onFcMultipackAccept();
        else await onFcMultipackDecline();
      } else if (currentState === STATES.BLACK_1) {
        if (action === 'accept') await onBlackAccept();
        else await onBlackDecline();
      } else if (currentState === STATES.FC_BLACK) {
        if (action === 'accept') await onFcBlackAccept();
        else await onFcBlackDecline();
      } else if (currentState === STATES.VIP) {
        if (action === 'accept') await onVipAccept();
        else await onVipDecline();
      }
    } catch (err) {
      console.error('[funnel] action error:', err);
      busy = false;
      // safest exit: close & open cart so user can recover
      closeModal();
      dispatchCartDrawerRefresh();
      dispatchCartDrawerOpen();
      return;
    }
    busy = false;
  }

  // ───── DOUBLE IT handler (BOGOF anchor: qty 2 → 4) ─────
  async function onDoubleItAccept() {
    // Anchor is qty=2 (BOGOF: 1 free). "Double it" consolidates to qty=4
    // → R1 frees 2. We also stamp _offer=doubleit so a future function rule
    // can apply an extra incentive (the price point is decided later).
    await setVariantTotalQty(product.variantId, 4, { '_offer': 'doubleit' });
    track('funnel_double_it_accept', { variant_id: product.variantId });
    stampFunnelOutcome('Funnel: Double It', 'accepted');
    closeModal();
    dispatchCartDrawerRefresh();
    dispatchCartDrawerOpen();
  }

  async function onDoubleItDecline() {
    track('funnel_double_it_decline', { variant_id: product.variantId });
    stampFunnelOutcome('Funnel: Double It', 'declined');
    // → FC_MULTIPACK if enabled + metafield, else open cart drawer
    if (canShowFcMultipack()) {
      await setState(STATES.FC_MULTIPACK, buildFcMultipackData());
    } else {
      closeModal();
      dispatchCartDrawerRefresh();
      dispatchCartDrawerOpen();
    }
  }

  async function onFcMultipackAccept() {
    // Add a SECOND line of the same product with _offer=finalchance.
    // R4 anti-abuse needs a non-FC multipack line in cart; the original
    // qty=1 or qty=2 line we wrote on ATC / MULTIPACK_A accept covers it.
    // Qty comes from the popup stepper (max 3, matched by R4's FC_MAX_UNITS).
    const stepper = body && body.querySelector('[data-funnel-qty]');
    const qty = stepper ? readStepperQty(stepper) : 1;
    await cartAdd(product.variantId, qty, { '_offer': 'finalchance' });
    track('funnel_fc_multipack_accept', { variant_id: product.variantId, quantity: qty });
    stampFunnelOutcome('Funnel: Last Chance Multipack', 'accepted');
    closeModal();
    dispatchCartDrawerRefresh();
    dispatchCartDrawerOpen();
  }

  async function onFcMultipackDecline() {
    track('funnel_fc_multipack_decline', { variant_id: product.variantId });
    stampFunnelOutcome('Funnel: Last Chance Multipack', 'declined');
    closeModal();
    dispatchCartDrawerRefresh();
    dispatchCartDrawerOpen();
  }

  // ───── BLACK chain handlers ─────
  // Black sibling lookup is server-side (Liquid) in snippets/funnel-cart-context.liquid,
  // emitted as window.Funnel.cartBlackSibling. Mirrors the multibuy2 family_tag pattern.
  async function onBlackAccept() {
    const bs = readCartBlackSibling();
    if (!bs) { finishToCheckout(); return; }
    // BOGOF black: add qty=2 → R1 (buy 1 get 1 free) on the BOGOF-tagged black.
    // No related/_offer property — R1 fires on tag + qty alone.
    await cartAdd(bs.variantId, 2);
    track('funnel_black_bogof_accept', { variant_id: bs.variantId });
    stampFunnelOutcome('Funnel: Black BOGOF', 'accepted');
    finishToCheckout();
  }
  async function onBlackDecline() {
    track('funnel_black_1_decline');
    stampFunnelOutcome('Funnel: Black BOGOF', 'declined');
    // FC Black fires whenever the black sibling has a final_chance.price metafield.
    // (Previous "skip if cart already has finalchance line" guard removed — R4 cap
    //  is being lifted in the function to allow multipack-FC + black-FC together.)
    if (canShowFcBlack()) {
      await setState(STATES.FC_BLACK, buildFcBlackData());
    } else {
      finishToCheckout();
    }
  }
  async function onFcBlackAccept() {
    const bs = readCartBlackSibling();
    if (!bs) { finishToCheckout(); return; }
    const qty = readCurrentBlackQty();
    // R4: stamp _offer="finalchance" → repriced per-unit via metafield
    await cartAdd(bs.variantId, qty, { '_offer': 'finalchance' });
    track('funnel_fc_black_accept', { variant_id: bs.variantId, quantity: qty });
    stampFunnelOutcome('Funnel: Last Chance Black', 'accepted');
    finishToCheckout();
  }
  async function onFcBlackDecline() {
    track('funnel_fc_black_decline');
    stampFunnelOutcome('Funnel: Last Chance Black', 'declined');
    finishToCheckout();
  }

  function goToCheckout() {
    closeModal();
    window.location.href = window.Funnel.checkoutUrl || '/checkout';
  }

  /**
   * The final gate before checkout. If VIP is enabled (and not already shown
   * this session / not already in cart), show the VIP popup; otherwise go
   * straight to checkout. Every black-chain terminal routes through here.
   *
   * TODO(IOSS): when the cart-limit guard lands, gate VIP here too — the
   * merchant said VIP should respect the limit (it's a physical-ish perk).
   * See IOSS-NOTES.md.
   */
  async function finishToCheckout() {
    const vip = window.Funnel.config && window.Funnel.config.vip;
    if (!vip || !vip.enabled || !vip.variantId || window.Funnel._vipShown) {
      goToCheckout();
      return;
    }
    // Don't re-offer if the VIP product is already in the cart.
    let cart = null;
    try { cart = await fetchCart(); } catch (e) {}
    if (cart && cart.items.some(i => Number(i.variant_id) === Number(vip.variantId))) {
      goToCheckout();
      return;
    }
    window.Funnel._vipShown = true;
    await setState(STATES.VIP, {});
  }

  async function onVipAccept() {
    const vip = window.Funnel.config && window.Funnel.config.vip;
    if (!vip || !vip.variantId) { goToCheckout(); return; }
    // Plain add + _vip property (carries to the order; hidden from customer UI).
    await cartAdd(vip.variantId, 1, { '_vip': 'true' });
    track('funnel_vip_accept', { variant_id: vip.variantId });
    stampFunnelOutcome('Funnel: VIP', 'accepted');
    goToCheckout();
  }
  async function onVipDecline() {
    track('funnel_vip_decline');
    stampFunnelOutcome('Funnel: VIP', 'declined');
    goToCheckout();
  }

  // ───── gates ─────
  // BOGOF mode: the "Double it" upsell only shows for BOGOF products AND only
  // when the merchant has switched it on in theme settings (ships OFF).
  function canShowDoubleIt() {
    if (!DOUBLE_IT_ENABLED) return false;
    return !!(product && Array.isArray(product.tags) && product.tags.indexOf('BOGOF') >= 0);
  }
  function canShowFcMultipack() {
    return !!(product && product.finalChancePrice && product.finalChancePrice > 0);
  }
  function canShowBlack() {
    const bs = readCartBlackSibling();
    return !!(bs && bs.variantId);
  }
  function canShowFcBlack() {
    const bs = readCartBlackSibling();
    return !!(bs && bs.finalChancePrice && bs.finalChancePrice > 0);
  }

  /**
   * Read the black-sibling data from the cart-drawer DOM at click time.
   *
   * The sibling lookup is server-side Liquid (snippets/funnel-cart-context.liquid).
   * When Enterprise refreshes the cart-drawer section it replaces innerHTML —
   * which does NOT execute inline <script> tags, so we can't rely on a script
   * setting window.Funnel.cartBlackSibling. Data attributes on a <div> DO
   * survive the replacement, so we read those instead.
   *
   * Also caches the result on window.Funnel.cartBlackSibling for any other
   * code paths that prefer the object form.
   */
  function readCartBlackSibling() {
    const el = document.getElementById('funnel-cart-context');
    if (!el) {
      window.Funnel.cartBlackSibling = null;
      return null;
    }
    const variantId = el.dataset.siblingVariantId;
    if (!variantId) {
      window.Funnel.cartBlackSibling = null;
      return null;
    }
    const fcRaw = el.dataset.siblingFcPrice;
    const sib = {
      productId: el.dataset.siblingProductId,
      variantId: variantId,
      handle: el.dataset.siblingHandle || '',
      title: el.dataset.siblingTitle || '',
      price: parseInt(el.dataset.siblingPrice, 10) || 0,
      image: el.dataset.siblingImage || '',
      finalChancePrice: fcRaw ? (parseInt(fcRaw, 10) || null) : null,
    };
    window.Funnel.cartBlackSibling = sib;
    return sib;
  }

  // ───── data builders ─────
  // The "marginal cost" appended to every CTA is the actual delta added
  // to the cart for accepting the offer — the most honest signal we can give.
  function ctaMarginal(cents) {
    if (!cents || cents <= 0) return '';
    return ' · +' + formatMoney(cents);
  }

  function buildDoubleItData() {
    // BOGOF anchor: qty=2 → pay for 1 (P for 2 items).
    // Double it: qty=4 → R1 frees 2 → pay for 2 (2P for 4 items).
    // Marginal added to cart = 1 unit price (the extra paid unit).
    // NOTE: with no extra incentive yet, the per-unit rate is identical to
    // the anchor (volume double). When the incentive price is decided, this
    // builder + the function rule on _offer=doubleit get updated together.
    const full = product.price;
    const fourTotal = full * 2;      // pay for 2 at qty=4 (BOGOF)
    const marginal = full;           // +1 paid unit vs the anchor
    return {
      image: product.image,
      imageAlt: product.title || '',
      'price-was': formatMoney(full * 4),   // notional value of 4 at full price
      'price-now': formatMoney(fourTotal),  // what they pay for 4
      'cta-marginal': ctaMarginal(marginal),
    };
  }
  function buildFcMultipackData() {
    const full = product.price;
    const fc = product.finalChancePrice;
    return {
      image: product.image,
      imageAlt: product.title || '',
      'title-price': formatMoney(fc),
      'price-was': formatMoney(full),
      'price-now': formatMoney(fc),
      'cta-marginal': ctaMarginal(fc),
    };
  }
  function buildBlackData() {
    // BOGOF black: pay for 1, get 2. Price-now = 1 unit (what they pay),
    // was = value of 2. No qty stepper for BOGOF (fixed buy-1-get-1).
    const bs = readCartBlackSibling() || {};
    const full = bs.price || 0;
    return {
      image: bs.image,
      imageAlt: bs.title || '',
      'black-title': bs.title || '',
      'price-was-total': formatMoney(full * 2),
      'price-now-total': formatMoney(full),
      'cta-marginal': ctaMarginal(full),
    };
  }
  function buildFcBlackData() {
    const bs = readCartBlackSibling() || {};
    const full = bs.price || 0;
    const fc = bs.finalChancePrice || 0;
    const qty = 1;
    return {
      image: bs.image,
      imageAlt: bs.title || '',
      'black-title': bs.title || '',
      'title-price': formatMoney(fc),
      'price-was-total': formatMoney(full * qty),
      'price-now-total': formatMoney(fc * qty),
      'cta-marginal': ctaMarginal(fc * qty),
    };
  }
  function refreshBlackTotals() {
    const stepper = body && body.querySelector('[data-funnel-qty]');
    if (!stepper) return;
    const qty = readStepperQty(stepper);
    // FC multipack: anchor product priced per-unit; update totals on qty change.
    if (currentState === STATES.FC_MULTIPACK) {
      const full = product.price;
      const now = product.finalChancePrice || 0;
      const wasEl = body.querySelector('[data-slot="price-was"]');
      const nowEl = body.querySelector('[data-slot="price-now"]');
      const ctaEl = body.querySelector('[data-slot="cta-marginal"]');
      if (wasEl) wasEl.textContent = formatMoney(full * qty);
      if (nowEl) nowEl.textContent = formatMoney(now * qty);
      if (ctaEl) ctaEl.textContent = ctaMarginal(now * qty);
      return;
    }
    const bs = readCartBlackSibling() || {};
    const full = bs.price || 0;
    let unitNow;
    if (currentState === STATES.BLACK_1) {
      unitNow = Math.round(full * 0.5);
    } else if (currentState === STATES.FC_BLACK) {
      unitNow = bs.finalChancePrice || 0;
    } else {
      return;
    }
    const wasEl = body.querySelector('[data-slot="price-was-total"]');
    const nowEl = body.querySelector('[data-slot="price-now-total"]');
    const ctaEl = body.querySelector('[data-slot="cta-marginal"]');
    if (wasEl) wasEl.textContent = formatMoney(full * qty);
    if (nowEl) nowEl.textContent = formatMoney(unitNow * qty);
    if (ctaEl) ctaEl.textContent = ctaMarginal(unitNow * qty);
  }

  // ───── ATC handling ─────
  async function handleAtcClick(triggerEl) {
    if (!product || !product.variantId) {
      log('no product context — falling back to native form submit');
      const form = triggerEl.closest('form');
      if (form) form.submit();
      return;
    }

    // Already added once this PDP visit? Don't double-add.
    // We detect by reading cart and checking if this variant is present
    // as a non-FC line — if so we DON'T add again, just resume the chain.
    let cart;
    try {
      cart = await fetchCart();
    } catch (e) {
      log('cart read failed, attempting add anyway');
    }

    const already = cart ? findLineByVariant(cart, product.variantId) : null;

    // BOGOF anchor: add qty=2 (R1 buy-1-get-1-free). Non-BOGOF: qty=1.
    const isBogof = !!(product.tags && product.tags.indexOf('BOGOF') >= 0);
    const anchorQty = isBogof ? 2 : 1;

    if (!already) {
      try {
        await cartAdd(product.variantId, anchorQty);
        track('funnel_atc', { variant_id: product.variantId, quantity: anchorQty });
        dispatchCartDrawerRefresh();
      } catch (e) {
        console.error('[funnel] ATC failed:', e);
        return;
      }
    } else {
      log('product already in cart, skipping add');
    }

    if (canShowDoubleIt()) {
      await setState(STATES.DOUBLE_IT, buildDoubleItData());
    } else if (canShowFcMultipack()) {
      // Double it off (or non-BOGOF) — fall back to Final Chance as the active offer.
      await setState(STATES.FC_MULTIPACK, buildFcMultipackData());
    } else {
      // No offer eligible — just open the cart drawer.
      dispatchCartDrawerOpen();
    }
  }

  // ───── analytics shim ─────
  function track(name, data) {
    try {
      if (window.dataLayer) window.dataLayer.push(Object.assign({ event: name }, data || {}));
      if (window.gtag) window.gtag('event', name, data || {});
    } catch (e) {}
  }

  // Record a popup outcome as a cart attribute so it lands on the order
  // ("Additional details"). Fire-and-forget with keepalive so it survives the
  // checkout navigation without delaying it (no await anywhere).
  function stampFunnelOutcome(key, value) {
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { [key]: value } }),
        keepalive: true
      });
    } catch (e) {}
  }

  // ───── BOGOF qty-mirror across split cart lines (lifted from custom.js) ─────
  // inkfactory's quantity-input.liquid emits qty-input__btn_bogofplus /
  // qty-input__btn_bogofminus (WITH the "f") and they're consistent with the
  // old custom.js, so the mirror works. We match those class names here.
  function bindBogofQtyMirror() {
    document.addEventListener('click', function (event) {
      const t = event.target;
      if (!t || !t.classList) return;
      const isPlus = t.classList.contains('qty-input__btn_bogofplus');
      const isMinus = t.classList.contains('qty-input__btn_bogofminus');
      if (!isPlus && !isMinus) return;

      const cartItem = t.closest('.cart-item');
      if (!cartItem) return;
      const variantId = cartItem.getAttribute('data-variant-id');
      if (!variantId) return;

      const qtyInput = isPlus ? t.previousElementSibling : t.nextElementSibling;
      let qty = parseInt(qtyInput && qtyInput.value, 10);
      if (!Number.isFinite(qty)) return;
      qty = isPlus ? qty + 1 : Math.max(0, qty - 1);

      document.querySelectorAll(
        `.cart-item[data-variant-id="${variantId}"] input[type="number"]`
      ).forEach(input => { input.value = qty; });

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: qty }),
      }).then(() => dispatchCartDrawerRefresh())
        .catch(err => console.error('[funnel] bogof mirror change error:', err));
    });
  }

  // ───── ATC trigger binding ─────
  function bindAtcTriggers() {
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest('[data-funnel-atc]');
      if (!trigger) return;
      event.preventDefault();
      handleAtcClick(trigger);
    });
  }

  // ───── checkout-click hook — black chain entry ─────
  // The cart-drawer's #reveal button is type=button (Funnel v3 change), so the
  // orchestrator OWNS navigation: either open the popup chain, or jump to /checkout.
  function bindCheckoutTriggers() {
    document.addEventListener('click', function (event) {
      const trigger = event.target.closest('[data-funnel-checkout]');
      if (!trigger) return;
      event.preventDefault();

      // No sibling → VIP (if enabled) then checkout
      if (!canShowBlack()) { finishToCheckout(); return; }
      // Already fired this drawer-open? skip black, go to VIP/checkout
      if (window.Funnel._blackChainFiredAt && (Date.now() - window.Funnel._blackChainFiredAt < 60_000)) {
        finishToCheckout();
        return;
      }
      window.Funnel._blackChainFiredAt = Date.now();
      // Close the cart drawer first so the popup owns the screen
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close', { bubbles: true }));
      setState(STATES.BLACK_1, buildBlackData());
    });
  }

  // ───── modal dismissal (backdrop click + ESC counts as decline) ─────
  function bindModalDismissals() {
    if (!modal) return;
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        // backdrop click
        if (busy) return;
        handleAction('decline');
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (currentState === STATES.IDLE) return;
      if (busy) return;
      event.preventDefault();
      handleAction('decline');
    });
  }

  // ───── boot ─────
  function boot() {
    bindAtcTriggers();
    bindCheckoutTriggers();
    // NOTE: inkfactory keeps its existing, proven BOGOF qty-mirror in custom.js
    // (consistent _bogofplus class names). We do NOT bind funnel.js's copy here
    // to avoid double-firing cart/change. (bindBogofQtyMirror stays defined but
    // unused on this store.)
    bindModalDismissals();
    bindBfcacheRecovery();
    log('booted', { product });
  }

  // ───── bfcache recovery ─────
  // When the customer hits Back from /checkout, the browser may restore this
  // page from the back-forward cache with a STALE cart snapshot — the header
  // cart count shows nothing and the drawer looks empty, even though the cart
  // is populated server-side. (Adding any item later fires a cart event that
  // re-syncs the UI, which is why "it comes back".) On a persisted pageshow we
  // refresh the cart UI so it reflects the true server cart immediately.
  function bindBfcacheRecovery() {
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      // Page restored from back-forward cache (e.g. Back from /checkout).
      // The whole DOM is a stale snapshot — header count, drawer, everything.
      // A forced reload re-renders all of it from the true server cart.
      window.location.reload();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ───── public API (for debugging) ─────
  window.Funnel.STATES = STATES;
  window.Funnel.setState = setState;
  window.Funnel.closeModal = closeModal;
  window.Funnel.handleAtcClick = handleAtcClick;
})();
